import { useState } from 'react';
import { MapPin, Clock, Users, Plane, ShoppingCart, ShoppingBag, Building2, MessageSquare, Bell, Trash2, Navigation, Route } from 'lucide-react';
import { DestinationType, RideCardProps } from '@/types';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';

const destinationIcons: Record<DestinationType, typeof Plane> = {
  airport: Plane,
  grocery: ShoppingCart,
  mall: ShoppingBag,
  campus: Building2,
  other: MapPin,
};

const destinationColors: Record<DestinationType, string> = {
  airport: 'bg-blue-100 text-blue-600',
  grocery: 'bg-green-100 text-accent',
  mall: 'bg-purple-100 text-purple-600',
  campus: 'bg-amber-100 text-amber-600',
  other: 'bg-gray-100 text-gray-600',
};

const RideCard = ({ 
  ride, 
  onRequestSeat, 
  onMessageDriver,
  onViewRequests,
  onMessageRider,
  onViewRiderProfile,
  onViewDriverProfile,
  onDeleteRide,
  showRequestButton = true,
  requestButtonLabel = 'Request',
  requestButtonDisabled = false,
  isDriverView = false,
  isFull = false,
  pendingRequests = 0,
  statusMode = isDriverView ? 'driver' : 'rider',
}: RideCardProps) => {
  const [isRidersPopoverOpen, setIsRidersPopoverOpen] = useState(false);
  const Icon = destinationIcons[ride.destination];
  const colorClass = destinationColors[ride.destination];
  const riders = ride.riders || [];
  const hasMultipleRiders = riders.length > 1;
  const driver = ride.driver;
  const driverId = driver?.id;
  const driverName = driver?.name ?? 'Unknown User';
  const driverRating = driver?.rating ?? 0;
  
  // Type guard to check if ride has requests (RideWithRequests)
  const hasRequests = 'requests' in ride && Array.isArray(ride.requests);
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTimeRange = (startTime: string, endTime: string) => {
    const formatSingle = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes}${ampm}`;
    };
    return `${formatSingle(startTime)}-${formatSingle(endTime)}`;
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'pending': return 'pending bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ongoing': return 'ongoing bg-green-500 text-white border-green-500';
      case 'ended': return 'ended bg-red-500 text-white border-red-500';
      case 'active': return 'active bg-blue-500 text-white border-blue-500';
      case 'full': return 'full bg-orange-500 text-white border-orange-500';
      case 'completed': return 'completed bg-gray-500 text-white border-gray-500';
      case 'cancelled': return 'cancelled bg-destructive text-destructive-foreground border-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const rawStatus = (
    statusMode === 'driver'
      ? ride.tripStatusDriver ?? ride.tripStatus ?? ride.status ?? 'pending'
      : ride.tripStatusRider ?? ride.tripStatus ?? ride.status ?? 'pending'
  ) as string;

  const computedStatus = (
    statusMode === 'driver' && (rawStatus === 'active' || rawStatus === 'full')
      ? 'pending'
      : rawStatus
  ) as string;

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const googleMapsSearchUrl = (address: string) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

  const handleOpenAddress = (address: string) => {
    window.open(googleMapsSearchUrl(address), '_blank', 'noopener,noreferrer');
  };

  const handleOpenRoute = () => {
    if (!navigator.geolocation) {
      toast.info('Location is unavailable in this browser. Opening the destination instead.');
      window.location.assign(googleMapsSearchUrl(ride.destinationName));
      return;
    }

    toast.info('Allow location access to build your route.');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const origin = `${coords.latitude},${coords.longitude}`;
        const routeUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(ride.destinationName)}&travelmode=driving`;
        window.location.assign(routeUrl);
      },
      () => {
        toast.error('Location access was denied. Opening Google Maps without a starting point.');
        const routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ride.destinationName)}&travelmode=driving`;
        window.location.assign(routeUrl);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const statusLabel = computedStatus === 'completed'
    ? 'Completed'
    : computedStatus === 'ended'
      ? 'Ended'
      : computedStatus === 'cancelled'
        ? 'Cancelled'
        : computedStatus === 'ongoing'
          ? 'Ongoing'
          : computedStatus === 'full'
            ? 'Full'
            : 'Pending';

  return (
    <article className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-warm">
      <div className="h-1.5 bg-gradient-to-r from-primary via-amber-400 to-accent" />
      <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={cn('rounded-xl p-2.5', colorClass)}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{ride.destination}</p>
            <button
              type="button"
              onClick={() => handleOpenAddress(ride.destinationName)}
              className="line-clamp-1 text-left font-display font-bold text-foreground hover:text-primary hover:underline"
              title="View destination in Google Maps"
            >
              {ride.destinationName}
            </button>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <span className="font-display text-xl font-bold text-primary">${ride.gasSplitCost}</span>
          <p className="text-[11px] text-muted-foreground">gas split</p>
        </div>
      </div>

      <div className="my-4 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 p-3">
        <div className="flex gap-3">
          <div className="flex flex-col items-center pt-1">
            <span className="h-2.5 w-2.5 rounded-full border-2 border-primary bg-card" />
            <span className="my-1 h-7 w-px bg-gradient-to-b from-primary to-accent" />
            <MapPin className="h-4 w-4 text-accent" />
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <button
              type="button"
              onClick={() => handleOpenAddress(ride.pickupLocation)}
              className="block max-w-full truncate text-left text-sm font-medium text-foreground hover:text-primary hover:underline"
              title="View pickup in Google Maps"
            >
              {ride.pickupLocation}
            </button>
            <button
              type="button"
              onClick={() => handleOpenAddress(ride.destinationName)}
              className="block max-w-full truncate text-left text-sm font-medium text-foreground hover:text-accent hover:underline"
              title="View destination in Google Maps"
            >
              {ride.destinationName}
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/50 pt-3 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5 text-primary" />
          <span>{formatDate(ride.date)}</span>
          <span>•</span>
          <span>{formatTimeRange(ride.startTime || '', ride.endTime || '')}</span>
          <Badge className={cn(getStatusClass(computedStatus), 'ml-auto text-[10px]')}>{statusLabel}</Badge>
        </div>
      </div>

      {isDriverView && (pendingRequests || 0) > 0 && (
        <button
          type="button"
          onClick={onViewRequests}
          className="mb-3 flex w-full items-center gap-2 rounded-lg border border-primary/20 bg-primary/10 p-2 text-left transition-colors hover:bg-primary/20"
        >
          <Bell className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-primary">{pendingRequests || 0} pending request{(pendingRequests || 0) !== 1 ? 's' : ''}</span>
        </button>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
        {!isDriverView ? (
          <>
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                className="rounded-full cursor-pointer hover:scale-110 transition-transform"
                onClick={() => driverId && onViewDriverProfile?.(driverId)}
                title={driverName}
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={driver?.avatar} alt={driverName} />
                  <AvatarFallback className="text-xs font-semibold bg-secondary text-secondary-foreground">
                    {getInitials(driverName)}
                  </AvatarFallback>
                </Avatar>
              </button>
              <div>
                <p className="max-w-24 truncate text-sm font-medium">{driverName}</p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <span className="text-amber-500">★</span>
                  <span>{driverRating}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className={cn(
                'hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium sm:flex',
                isFull || ride.seatsAvailable === 0
                  ? "bg-muted text-muted-foreground" 
                  : "bg-secondary text-accent"
              )}>
                <Users className="w-4 h-4" />
                <span>{isFull ? 'Full' : `${ride.seatsAvailable} left`}</span>
              </div>

              {showRequestButton && !isFull && ride.seatsAvailable > 0 && (
                <button
                  type="button"
                  onClick={onRequestSeat}
                  disabled={requestButtonDisabled}
                  className={cn(
                    "px-4 py-1.5 rounded-full text-sm font-semibold transition-colors shadow-warm",
                    requestButtonDisabled
                      ? "bg-muted text-muted-foreground cursor-not-allowed shadow-none"
                      : "bg-primary text-primary-foreground hover:bg-primary/90"
                  )}
                >
                  {requestButtonLabel}
                </button>
              )}
              
              {onMessageDriver && driverId && (
                <button
                  type="button"
                  onClick={onMessageDriver}
                  className="p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                >
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              {hasMultipleRiders ? (
                <Popover open={isRidersPopoverOpen} onOpenChange={setIsRidersPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 rounded-xl -m-2 p-2 text-left hover:bg-muted/60 transition-colors"
                      title="View riders"
                    >
                      <div className="flex -space-x-2">
                        {riders.slice(0, 3).map((rider, idx) => (
                          <div
                            key={rider.id}
                            className="w-8 h-8 rounded-full border-2 border-background overflow-hidden bg-secondary"
                            style={{ zIndex: 3 - idx }}
                          >
                            <Avatar className="w-full h-full">
                              <AvatarImage src={rider.avatar} alt={rider.name} />
                              <AvatarFallback className="text-xs font-semibold bg-secondary text-secondary-foreground">
                                {getInitials(rider.name)}
                              </AvatarFallback>
                            </Avatar>
                          </div>
                        ))}
                        {riders.length > 3 && (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center border-2 border-background">
                            <span className="text-xs text-muted-foreground">+{riders.length - 3}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{riders.length}/{ride.seatsTotal} riders</p>
                        <p className="text-xs text-muted-foreground">{ride.seatsAvailable} seats left</p>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[320px] p-3">
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Riders on this trip</p>
                        <p className="text-xs text-muted-foreground">Open a profile or start a message from here.</p>
                      </div>
                      <div className="space-y-2">
                        {riders.map((rider) => (
                          <div
                            key={rider.id}
                            className="flex items-center gap-3 rounded-xl border border-border/60 bg-card px-3 py-2"
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setIsRidersPopoverOpen(false);
                                onViewRiderProfile?.(rider.id);
                              }}
                              className="flex items-center gap-3 flex-1 min-w-0 text-left"
                              title={`View ${rider.name}'s profile`}
                            >
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={rider.avatar} alt={rider.name} />
                                <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-semibold">
                                  {getInitials(rider.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{rider.name}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {rider.major || 'Rider'}
                                </p>
                              </div>
                            </button>
                            {onMessageRider && (
                              <button
                                type="button"
                                onClick={() => {
                                  setIsRidersPopoverOpen(false);
                                  onMessageRider(rider.id);
                                }}
                                className="shrink-0 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
                              >
                                Message
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <>
                  <div className="flex -space-x-2">
                    {riders.slice(0, 3).map((rider, idx) => (
                      <div
                        key={rider.id}
                        className="w-8 h-8 rounded-full border-2 border-background overflow-hidden bg-secondary cursor-pointer hover:scale-110 transition-transform"
                        style={{ zIndex: 3 - idx }}
                        onClick={() => onViewRiderProfile?.(rider.id)}
                        title={rider.name}
                      >
                        <Avatar className="w-full h-full">
                          <AvatarImage src={rider.avatar} alt={rider.name} />
                          <AvatarFallback className="text-xs font-semibold bg-secondary text-secondary-foreground">
                            {getInitials(rider.name)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{riders.length}/{ride.seatsTotal} riders</p>
                    <p className="text-xs text-muted-foreground">{ride.seatsAvailable} seats left</p>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              {hasRequests && onViewRequests && (
                <button
                  type="button"
                  onClick={onViewRequests}
                  className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium hover:bg-primary/20 transition-colors"
                >
                  Manage
                </button>
              )}
              {onDeleteRide && (
                <button
                  type="button"
                  onClick={onDeleteRide}
                  className="p-2 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                  title="Delete ride"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        onClick={handleOpenRoute}
        className={cn(
          'mt-3 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors',
          isDriverView
            ? 'bg-accent text-accent-foreground hover:bg-accent/90'
            : 'bg-primary/10 text-primary hover:bg-primary/20',
        )}
      >
        {isDriverView ? <Navigation className="h-4 w-4" /> : <Route className="h-4 w-4" />}
        {isDriverView ? 'Route to destination' : 'Verify route'}
      </button>
      </div>
    </article>
  );
};

export default RideCard;
